/**
 * `<InviteMemberDialog>` — invite-by-email form launched from
 * `<MembersSettings>`. The default role is `member` per FD R6.
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/nfr-design/frontend-architecture.md § 5.3`.
 *
 * The schema uses `role: roleSchema.default("member")`, which leaves
 * `role` optional on the input side while the parsed output guarantees
 * it. react-hook-form types the form against the input shape; the
 * mutation receives the parsed output. Both shapes are split below.
 *
 * On success: invalidates the invitations + members queries (so the
 * pending-invitation row appears immediately), toasts, closes the
 * dialog, resets the form.
 */

import { authClient, type AuthClientError } from "@cometchat-ai/auth/client";
import { inviteMemberSchema } from "@cometchat-ai/auth/shared";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Mail, X } from "./icons";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import { FINAL_FONT } from "../layouts/final-polish";
import { formatApiError } from "../lib/format-error";
import { listRoles, rolesKeys } from "../settings/api";

type InviteMemberFormValues = z.input<typeof inviteMemberSchema>;
type InviteMemberSubmitValues = z.output<typeof inviteMemberSchema>;

export interface InviteMemberDialogProps {
  workspaceSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function humanizeRole(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
}

export function InviteMemberDialog({ workspaceSlug, open, onOpenChange }: InviteMemberDialogProps) {
  const { t } = useTranslation("workspace");
  const queryClient = useQueryClient();

  const rolesQuery = useQuery({
    queryKey: rolesKeys.list(workspaceSlug),
    queryFn: () => listRoles(workspaceSlug),
    staleTime: 30_000,
    enabled: open,
  });
  const builtinRoles = rolesQuery.data?.builtin ?? [
    { name: "member", permissions: [] },
    { name: "admin", permissions: [] },
  ];
  const customRoles = rolesQuery.data?.custom ?? [];

  const form = useForm<InviteMemberFormValues, unknown, InviteMemberSubmitValues>({
    resolver: zodResolver(inviteMemberSchema),
    defaultValues: { email: "", role: "member" },
  });

  // Reset whenever the dialog re-opens — Sonner-style stale state guard.
  useEffect(() => {
    if (open) form.reset({ email: "", role: "member" });
  }, [open, form]);

  const mutation = useMutation({
    mutationFn: (values: InviteMemberSubmitValues) =>
      authClient.inviteMember(workspaceSlug, values),
    onSuccess: (result, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["invitations", workspaceSlug] });
      void queryClient.invalidateQueries({ queryKey: ["members", workspaceSlug] });
      // An existing org member is added straight to the workspace (no email);
      // a new person gets an invitation to accept.
      toast.success(
        result.outcome === "added"
          ? t("invite.addedToast", { email: vars.email })
          : t("invite.toast", { email: vars.email }),
      );
      onOpenChange(false);
    },
    onError: (err: AuthClientError) => {
      toast.error(t("invite.failed"), { description: formatApiError(err) });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="invite-member-dialog"
        style={{ fontFamily: FINAL_FONT }}
        className="max-w-md gap-0 rounded-3xl p-0 sm:rounded-3xl"
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
            data-testid="invite-member-form"
            noValidate
          >
            <DialogHeader className="space-y-1 px-5 pb-4 pt-5 text-left">
              <DialogTitle className="text-base font-medium tracking-[0.01em]">
                {t("invite.title")}
              </DialogTitle>
              <DialogDescription className="text-[13px] text-muted-foreground">
                {t("invite.subtitle")}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 px-5 pb-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="text-[13px] font-medium text-foreground">
                      {t("invite.email.label")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="email"
                        placeholder={t("invite.email.placeholder")}
                        data-testid="invite-member-email"
                        className="h-9 rounded-lg text-[13px] md:text-[13px] placeholder:text-[color:var(--color-fg-subtle)]"
                        style={{ fontFamily: "inherit" }}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="text-[13px] font-medium text-foreground">
                      {t("invite.role.label")}
                    </FormLabel>
                    <Select
                      value={field.value ?? "member"}
                      onValueChange={(value) => field.onChange(value)}
                    >
                      <FormControl>
                        <SelectTrigger
                          data-testid="invite-member-role"
                          className="h-9 rounded-lg text-[13px]"
                        >
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {builtinRoles.map((r) => (
                          <SelectItem
                            key={r.name}
                            value={r.name}
                            data-testid={`invite-member-role-${r.name}`}
                          >
                            {r.name === "admin"
                              ? t("members.roles.admin")
                              : r.name === "member"
                                ? t("members.roles.member")
                                : humanizeRole(r.name)}
                          </SelectItem>
                        ))}
                        {customRoles.map((c) => (
                          <SelectItem
                            key={c.id}
                            value={c.id}
                            data-testid={`invite-member-role-${c.id}`}
                          >
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription className="text-xs text-muted-foreground">
                      {field.value === "admin"
                        ? t("invite.role.adminHint")
                        : t("invite.role.memberHint")}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="flex-row gap-2 px-5 pb-5 pt-4">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={mutation.isPending}
                data-testid="invite-member-cancel"
                className="h-8 flex-1 gap-1.5 rounded-md text-[13px] hover:bg-secondary-hover"
              >
                <X className="h-4 w-4" aria-hidden="true" />
                {t("invite.cancel")}
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={mutation.isPending}
                data-testid="invite-member-submit"
                className="h-8 flex-1 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
              >
                {mutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Mail className="h-4 w-4" aria-hidden="true" />
                )}
                {mutation.isPending ? t("invite.submitting") : t("invite.submit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
